import { decodeState } from "./codec";

export interface LibraryEntry {
  code: string;
  savedAt: number;
  variationId: string;
  nickname: string;
  universeMode: "separate" | "shared";
}

export const LIBRARY_STORAGE_KEY = "ql.infiniteSelves.library.v1";
const MAX_LIBRARY_ENTRIES = 12;

/**
 * Read the library list from localStorage.
 * Guarded against blocked storage, invalid JSON, and non-valid state codes.
 * Ensures newest first, deduplicated by code, capped at 12 entries.
 */
export function getLibrary(): LibraryEntry[] {
  try {
    if (typeof window === "undefined" || !window.localStorage) {
      return [];
    }
    const raw = window.localStorage.getItem(LIBRARY_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    const validEntries: LibraryEntry[] = [];
    const seenCodes = new Set<string>();

    for (const item of parsed) {
      if (
        item &&
        typeof item === "object" &&
        typeof item.code === "string" &&
        typeof item.variationId === "string" &&
        (item.universeMode === "separate" || item.universeMode === "shared")
      ) {
        if (seenCodes.has(item.code)) continue;

        // Strictly validate code with decodeState per spec
        const decoded = decodeState(item.code);
        if (!decoded) continue;

        seenCodes.add(item.code);
        validEntries.push({
          code: item.code,
          savedAt: typeof item.savedAt === "number" ? item.savedAt : Date.now(),
          variationId: item.variationId,
          nickname: typeof item.nickname === "string" ? item.nickname : "",
          universeMode: item.universeMode,
        });

        if (validEntries.length >= MAX_LIBRARY_ENTRIES) break;
      }
    }

    return validEntries;
  } catch {
    return [];
  }
}

/**
 * Save an entry to the library.
 * Deduplicates by code (updating to top with latest savedAt), caps at 12.
 */
export function saveToLibrary(entry: LibraryEntry): void {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;

    // Validate code before storing
    if (!decodeState(entry.code)) return;

    const current = getLibrary();
    const filtered = current.filter((item) => item.code !== entry.code);
    const updated: LibraryEntry[] = [
      {
        code: entry.code,
        savedAt: entry.savedAt || Date.now(),
        variationId: entry.variationId,
        nickname: entry.nickname || "",
        universeMode: entry.universeMode,
      },
      ...filtered,
    ].slice(0, MAX_LIBRARY_ENTRIES);

    window.localStorage.setItem(LIBRARY_STORAGE_KEY, JSON.stringify(updated));
  } catch {
    // Ignore quota or security errors
  }
}

/**
 * Remove an entry by code.
 */
export function removeFromLibrary(code: string): void {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    const current = getLibrary();
    const updated = current.filter((item) => item.code !== code);
    window.localStorage.setItem(LIBRARY_STORAGE_KEY, JSON.stringify(updated));
  } catch {
    // Ignore
  }
}

/**
 * Clear all entries from the library.
 */
export function clearLibrary(): void {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.removeItem(LIBRARY_STORAGE_KEY);
  } catch {
    // Ignore
  }
}
